import { StopOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Card,
  Checkbox,
  Empty,
  Flex,
  Grid,
  Input,
  List,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnsType, TableProps } from 'antd';
import { useNavigate } from 'react-router';
import { useEstadoDeLista } from '@titularis/ui';
import {
  ROLE_DESCRIPTION,
  ROLE_LABEL,
  ROLES,
  type EtapaDaPessoa,
  type OrdemDasPessoas,
  type PessoaNaLista,
  type RecorrenciaDaPessoa,
  type Role,
  type SituacaoDaPessoa,
} from '@titularis/api-client';
import { api } from '../../api';
import { rotuloDoCanal } from '../origem';
import { emReais, formatarData } from '../../dominio';
import {
  CONTA_DE_TESTE_AJUDA,
  CONTA_DE_TESTE_COR,
  CONTA_DE_TESTE_LABEL,
  ETAPA_AJUDA,
  ETAPA_COR,
  ETAPA_LABEL,
  haQuantoTempo,
  SITUACAO_COR,
  SITUACAO_LABEL,
  taxaDeAcerto,
} from './vocabulario';
import { lerPorPagina, POR_PAGINA_PADRAO, TAMANHOS_DE_PAGINA } from './tamanhoDaPagina';
import { FaixaDeSemanas } from './FaixaDeSemanas';
import {
  diasPorExtenso,
  ORDEM_LABEL,
  ORDENS,
  RECORRENCIA_AJUDA,
  RECORRENCIA_LABEL,
  RECORRENCIAS,
} from './recorrencia';

const ETAPAS: EtapaDaPessoa[] = [
  'sem_confirmacao',
  'sem_uso',
  'estudando',
  'no_limite',
  'comprou',
];

const SITUACOES: SituacaoDaPessoa[] = ['gratuito', 'assinante', 'vencido', 'backoffice'];

/**
 * A coluna que ordena cada ordem da lista. É por esta chave que o clique no
 * cabeçalho vira `?ordem=` na URL, e a URL volta a ser seta no cabeçalho.
 */
const ORDEM_DA_COLUNA: Record<string, OrdemDasPessoas> = {
  recorrencia: 'dias_de_estudo',
  ultima: 'ultimo_estudo',
  criadaEm: 'entrada',
};

/**
 * A lista de pessoas: uma linha por gente, e não por direito nem por canal.
 *
 * **É a tela que faltava.** Assinantes lista uma linha por par pessoa-produto,
 * então quem comprou duas leis aparecia duas vezes e quem não comprou nada não
 * aparecia; Administradores listava toda conta e sabia dizer só o papel dela;
 * Origem contava canais e não dizia quem eram. Nenhuma das três respondia "essa
 * pessoa que criou conta ontem, fez o quê?" - e cruzá-las era copiar e-mail de
 * uma aba para outra.
 *
 * As colunas respondem, da esquerda para a direita, às perguntas na ordem em
 * que elas aparecem: **quem é**, **de onde veio**, **onde parou**, **o que
 * tem e quanto pagou**, **o que fez** e **se voltou**. Clicar na linha abre a
 * pessoa.
 *
 * **A ordem de entrada é o padrão, e não a única.** Ela responde "quem chegou
 * depois do post"; "quem está usando de verdade" pede a lista ordenada pela
 * recorrência ou pelo último estudo, e é o cabeçalho dessas colunas que
 * reordena - a lista inteira, no servidor, e não só a página que está na tela.
 */
export function ListaDePessoas({ canais }: { canais: string[] }) {
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { ler, filtrar, irParaPagina, busca: enderecoAtual } = useEstadoDeLista();

  const busca = ler.texto('busca') ?? '';
  const etapa = ETAPAS.find((valor) => valor === ler.texto('etapa'));
  const situacao = SITUACOES.find((valor) => valor === ler.texto('situacao'));
  const canal = ler.texto('canal');
  const papel = ROLES.find((valor) => valor === ler.texto('papel'));
  const recorrencia = RECORRENCIAS.find((valor) => valor === ler.texto('recorrencia'));
  // Ausente, ou qualquer coisa que não se entende, é a ordem de entrada.
  const ordem = ORDENS.find((valor) => valor === ler.texto('ordem')) ?? 'entrada';
  const page = ler.pagina();
  const porPagina = lerPorPagina(ler.texto('porPagina'));

  /*
   * **As contas internas ficam de fora por padrão** - a casa e as de teste. A
   * lista é lida junto com o funil, e o funil só conta alunos de verdade: com
   * as duas populações diferentes, "Criaram conta: 19" em cima e vinte e quatro
   * linhas embaixo é a tela se contradizendo.
   *
   * O filtro que pede a casa com todas as letras - nível "Administrador",
   * acesso "Equipe" - vence o padrão, e a caixa fica marcada e travada para
   * dizer isso. A API aplica a mesma regra (`pedeContaDaCasa`); aqui ela só
   * decide o que a caixa mostra.
   */
  const pedeContaDaCasa = situacao === 'backoffice' || (papel !== undefined && papel !== 'user');
  const mostrarInternas = pedeContaDaCasa || ler.texto('internas') === 'mostrar';

  const pessoas = useQuery({
    queryKey: [
      'pessoas',
      {
        busca,
        etapa,
        situacao,
        canal,
        papel,
        recorrencia,
        ordem,
        mostrarInternas,
        page,
        porPagina,
      },
    ],
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.listPeople({
        ...(mostrarInternas ? {} : { semInternas: true }),
        ...(busca ? { busca } : {}),
        ...(etapa ? { etapa } : {}),
        ...(situacao ? { situacao } : {}),
        ...(canal ? { canal } : {}),
        ...(papel ? { papel } : {}),
        ...(recorrencia ? { recorrencia } : {}),
        ...(ordem === 'entrada' ? {} : { ordem }),
        page,
        pageSize: porPagina,
      }),
  });

  /*
   * As três colunas que ordenam, e só na direção decrescente: "mais dias
   * primeiro", "o último estudo primeiro", "a entrada mais recente primeiro". A
   * crescente de qualquer uma delas põe no topo justamente quem não interessa.
   * Clicar de novo na coluna ativa devolve a lista à ordem de entrada, e o
   * balão diz isso em vez de repetir o que a coluna já está fazendo.
   */
  const ordenavel = (coluna: keyof typeof ORDEM_DA_COLUNA, dica: string) => {
    const ativa = ORDEM_DA_COLUNA[coluna] === ordem;
    const padrao = ORDEM_DA_COLUNA[coluna] === 'entrada';

    return {
      sorter: true,
      sortDirections: ['descend' as const],
      sortOrder: ativa ? ('descend' as const) : null,
      showSorterTooltip:
        ativa && padrao ? false : { title: ativa ? 'Voltar à ordem de entrada' : dica },
    };
  };

  const colunas: TableColumnsType<PessoaNaLista> = [
    {
      title: 'Pessoa',
      key: 'pessoa',
      /*
       * Largura própria e reticências no e-mail. Sem elas, um endereço longo
       * quebrava em três linhas e a linha da tabela ficava com o triplo da
       * altura - com doze contas na tela, uma página inteira para doze nomes.
       */
      width: 210,
      render: (_: unknown, linha) => (
        <Space orientation="vertical" size={0} style={{ maxWidth: 178 }}>
          <Space size={6}>
            <Typography.Text strong ellipsis>
              {linha.nome}
            </Typography.Text>
            {linha.suspensa ? (
              <Tooltip title="Conta suspensa: não consegue entrar.">
                <StopOutlined style={{ color: token.colorError }} />
              </Tooltip>
            ) : null}
            {linha.situacao === 'backoffice' ? (
              <Tag color={SITUACAO_COR.backoffice}>{ROLE_LABEL[linha.papel as Role]}</Tag>
            ) : null}
            {linha.contaDeTeste ? (
              <Tooltip title={CONTA_DE_TESTE_AJUDA}>
                <Tag color={CONTA_DE_TESTE_COR}>{CONTA_DE_TESTE_LABEL}</Tag>
              </Tooltip>
            ) : null}
          </Space>
          <Typography.Text type="secondary" ellipsis={{ tooltip: linha.email }}>
            {linha.email}
          </Typography.Text>
        </Space>
      ),
    },
    {
      /*
       * O canal em cima e o meio e a campanha embaixo, e não os três numa linha
       * cortada: a linha da tabela já tem duas alturas por causa do nome e do
       * e-mail, e em 140px "instagram / social / enac4" virava "instagram /
       * so...", que esconde justamente a campanha. O balão devolve o rótulo
       * inteiro, como antes.
       */
      title: 'Origem',
      key: 'canal',
      width: 140,
      render: (_: unknown, linha) => {
        const [canal, ...detalhe] = [
          linha.canal?.source,
          linha.canal?.medium,
          linha.canal?.campaign,
        ].filter(Boolean);

        if (!canal) {
          return <Typography.Text type="secondary">{rotuloDoCanal(null)}</Typography.Text>;
        }

        return (
          <Tooltip title={rotuloDoCanal(linha.canal)}>
            <Space orientation="vertical" size={0} style={{ maxWidth: 108 }}>
              <Typography.Text ellipsis>{canal}</Typography.Text>
              {detalhe.length > 0 ? (
                <Typography.Text type="secondary" ellipsis>
                  {detalhe.join(' / ')}
                </Typography.Text>
              ) : null}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Etapa',
      key: 'etapa',
      width: 170,
      render: (_: unknown, linha) => (
        <Tooltip title={ETAPA_AJUDA[linha.etapa]}>
          <Tag color={ETAPA_COR[linha.etapa]}>{ETAPA_LABEL[linha.etapa]}</Tag>
        </Tooltip>
      ),
    },
    {
      /*
       * O acesso e o que ele custou, na mesma célula. Até 29/09/2026 o valor
       * pago tinha coluna própria, no fim da tabela; quando a Recorrência
       * entrou, a tabela passou de 1.555px numa área de 1.247 - e as três
       * últimas colunas, a de entrada inclusive, sumiam atrás de uma rolagem
       * que ninguém adivinha. Juntos, eles se leem como uma coisa só:
       * "Assinante, pagou R$ 519".
       */
      title: 'Acesso',
      key: 'situacao',
      width: 125,
      render: (_: unknown, linha) => (
        <Space orientation="vertical" size={2}>
          {linha.situacao === 'backoffice' ? (
            <Typography.Text type="secondary">-</Typography.Text>
          ) : (
            <Space size={4}>
              <Tag color={SITUACAO_COR[linha.situacao]}>{SITUACAO_LABEL[linha.situacao]}</Tag>
              {linha.acessosAtivos > 1 ? (
                <Typography.Text type="secondary">{linha.acessosAtivos}</Typography.Text>
              ) : null}
            </Space>
          )}
          {linha.centavos > 0 ? (
            <Tooltip title="Quanto esta conta já pagou, somando as compras.">
              {/* Sem o `nowrap`, "R$ 519,00" quebrava entre o 519 e o segundo zero. */}
              <Typography.Text strong style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                {emReais(linha.centavos)}
              </Typography.Text>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      /*
       * Os três números numa coluna só, e não em três: eles se leem juntos -
       * "respondeu 14, abriu 3 resoluções, leu 0 artigo" é uma frase - e três
       * colunas de inteiros pequenos gastariam metade da largura da tabela para
       * dizer o mesmo.
       */
      title: 'Uso',
      key: 'uso',
      width: 200,
      render: (_: unknown, linha) => {
        const taxa = taxaDeAcerto(linha.respostas, linha.acertos);

        if (linha.respostas + linha.resolucoes + linha.artigos === 0) {
          return <Typography.Text type="secondary">nada ainda</Typography.Text>;
        }

        return (
          <Space orientation="vertical" size={0}>
            <Typography.Text>
              {linha.respostas} resp. · {linha.resolucoes} resol. · {linha.artigos} art.
            </Typography.Text>
            {taxa === null ? null : (
              <Typography.Text type="secondary">
                {Math.round(taxa * 100)}% de acerto
              </Typography.Text>
            )}
          </Space>
        );
      },
    },
    {
      /*
       * A faixa em cima e o total embaixo. O total responde "quanto voltou" e a
       * faixa responde "quando": cinco dias na semana do cadastro e cinco dias
       * em cinco semanas são o mesmo número, e só a faixa separa a maratona de
       * um fim de semana do hábito.
       */
      title: 'Recorrência',
      key: 'recorrencia',
      width: 155,
      ...ordenavel('recorrencia', 'Quem estudou em mais dias primeiro'),
      render: (_: unknown, linha) => (
        <Flex vertical gap={6}>
          <FaixaDeSemanas semanas={linha.semanas} />
          <Typography.Text
            type={linha.diasDeEstudo === 0 ? 'secondary' : undefined}
            style={{ fontSize: 12 }}
          >
            {diasPorExtenso(linha.diasDeEstudo)}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Última vez',
      key: 'ultima',
      width: 130,
      ...ordenavel('ultima', 'Quem estudou por último primeiro'),
      render: (_: unknown, linha) => (
        <Tooltip
          title={
            <>
              Estudou: {formatarData(linha.ultimaAtividade)}
              <br />
              Entrou: {formatarData(linha.ultimoAcesso)}
            </>
          }
        >
          <Typography.Text type={linha.ultimaAtividade ? undefined : 'secondary'}>
            {haQuantoTempo(linha.ultimaAtividade)}
          </Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: 'Entrou',
      key: 'criadaEm',
      width: 110,
      ...ordenavel('criadaEm', 'Voltar à ordem de entrada'),
      render: (_: unknown, linha) => (
        <Tooltip title={formatarData(linha.criadaEm)}>
          <Typography.Text type="secondary">{haQuantoTempo(linha.criadaEm)}</Typography.Text>
        </Tooltip>
      ),
    },
  ];

  /*
   * Só a ordenação passa por aqui. A troca de página tem o próprio `onChange`,
   * na paginação, e ordenar não o dispara - o Antd só recomeça a paginação
   * sozinho quando o filtro é da própria tabela. Quem recomeça aqui é o
   * `filtrar`, que tira a página da URL: a página 3 de outra ordem é outra gente.
   */
  const ordenar: TableProps<PessoaNaLista>['onChange'] = (
    _paginacao,
    _filtros,
    sorter,
    { action },
  ) => {
    if (action !== 'sort') return;

    const escolhida = Array.isArray(sorter) ? sorter[0] : sorter;
    const nova = escolhida?.order ? ORDEM_DA_COLUNA[String(escolhida.columnKey)] : undefined;
    filtrar({ ordem: nova === 'entrada' ? undefined : nova });
  };

  const abrir = (id: string) => {
    const ficha = `/pessoas/${encodeURIComponent(id)}`;
    // Sem filtro nenhum não há o que restaurar, e um `?de=` vazio na barra de
    // endereço é sujeira que a pessoa copia junto com o link.
    void navigate(enderecoAtual ? `${ficha}?de=${encodeURIComponent(enderecoAtual)}` : ficha);
  };

  /*
   * O tamanho das linhas que estão na tela, e não o da URL: com
   * `keepPreviousData`, logo depois da troca a tabela ainda mostra a consulta
   * anterior, e o paginador tem de descrever o que está mostrando.
   */
  const tamanhoNaTela = pessoas.data?.pageSize ?? porPagina;
  const total = pessoas.data?.total ?? 0;

  const paginacao = {
    current: pessoas.data?.page ?? page,
    pageSize: tamanhoNaTela,
    total,
    showSizeChanger: true,
    pageSizeOptions: TAMANHOS_DE_PAGINA,
    /*
     * Trocar de página e trocar o tamanho chegam os dois por aqui, e é de
     * propósito que `onShowSizeChange` fique de fora: na troca de tamanho o
     * Ant Design dispara os dois callbacks, e a segunda escrita na URL - que só
     * conhece a página - apagaria o tamanho novo. Uma escrita por interação.
     */
    onChange: (pagina: number, tamanho: number) => {
      if (tamanho !== tamanhoNaTela) {
        // Outro tamanho, outras pessoas em cada página: a lista recomeça.
        filtrar({ porPagina: tamanho === POR_PAGINA_PADRAO ? undefined : tamanho });
        return;
      }
      irParaPagina(pagina);
    },
  };

  /*
   * **O paginador só some quando nenhuma escolha mudaria nada**: quando até o
   * menor tamanho cabe a lista inteira. O `hideOnSinglePage` esconderia cedo
   * demais - quem pusesse cem por página e filtrasse até sobrarem trinta
   * perderia o seletor junto com as páginas, e voltar a vinte exigiria editar
   * a URL.
   */
  const comPaginacao = total > Math.min(...TAMANHOS_DE_PAGINA);

  const vazio = (
    <Empty
      description={
        busca || etapa || situacao || canal || papel || recorrencia
          ? 'Ninguém com esses filtros.'
          : mostrarInternas
            ? 'Nenhuma conta criada ainda.'
            : 'Nenhuma conta de aluno ainda.'
      }
    />
  );

  /**
   * **No celular a tabela vira lista**, como no painel de Progresso e pelo mesmo
   * motivo: oito colunas em 390px não encolhem, elas rolam para o lado - e uma
   * tabela que rola escondida dentro de um cartão é uma tabela que ninguém lê no
   * telefone.
   *
   * Esta é a tela que mais se abre do celular na semana de lançamento - "quem
   * entrou depois do post?" -, e ela não pode pedir zoom. O que sobra é o que
   * responde essa pergunta: quem é, em que etapa parou, por onde veio e há
   * quanto tempo apareceu. O resto está na ficha, a um toque.
   */
  const noCelular = (
    <List
      size="small"
      dataSource={pessoas.data?.items ?? []}
      loading={pessoas.isPending}
      locale={{ emptyText: vazio }}
      renderItem={(linha) => (
        <List.Item
          style={{ cursor: 'pointer', paddingInline: 0 }}
          onClick={() => abrir(linha.id)}
          extra={
            <Flex vertical align="flex-end" gap={2} style={{ minWidth: 76 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {haQuantoTempo(linha.criadaEm)}
              </Typography.Text>
              {linha.centavos > 0 ? (
                <Typography.Text strong style={{ fontSize: 12 }}>
                  {emReais(linha.centavos)}
                </Typography.Text>
              ) : null}
            </Flex>
          }
        >
          <List.Item.Meta
            title={
              <Space size={6} wrap>
                <Typography.Text strong>{linha.nome}</Typography.Text>
                <Tag color={ETAPA_COR[linha.etapa]} style={{ marginInlineEnd: 0 }}>
                  {ETAPA_LABEL[linha.etapa]}
                </Tag>
                {linha.situacao === 'assinante' || linha.situacao === 'vencido' ? (
                  <Tag color={SITUACAO_COR[linha.situacao]} style={{ marginInlineEnd: 0 }}>
                    {SITUACAO_LABEL[linha.situacao]}
                  </Tag>
                ) : null}
                {linha.contaDeTeste ? (
                  <Tag color={CONTA_DE_TESTE_COR} style={{ marginInlineEnd: 0 }}>
                    {CONTA_DE_TESTE_LABEL}
                  </Tag>
                ) : null}
              </Space>
            }
            description={
              <Space orientation="vertical" size={0} style={{ width: '100%' }}>
                <Typography.Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                  {linha.email}
                </Typography.Text>
                <Typography.Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                  {rotuloDoCanal(linha.canal)}
                  {linha.respostas + linha.resolucoes + linha.artigos > 0
                    ? ` · ${linha.respostas} resp. · ${linha.resolucoes} resol.`
                    : ' · nada ainda'}
                </Typography.Text>
                {/*
                  A faixa vem junto no celular, e é a única coluna nova que
                  cabe: ela é a resposta a "quem está voltando", e essa pergunta
                  também se faz do telefone.
                */}
                <Flex align="center" gap={8} style={{ marginTop: 4 }}>
                  <FaixaDeSemanas semanas={linha.semanas} tamanho={10} />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {diasPorExtenso(linha.diasDeEstudo)}
                  </Typography.Text>
                </Flex>
              </Space>
            }
          />
        </List.Item>
      )}
    />
  );

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Flex gap={12} wrap>
        {/*
          No celular não há cabeçalho de tabela para clicar, e a ordem vira
          seletor. É o mesmo `?ordem=` que o cabeçalho escreve no computador.
        */}
        {telas.md ? null : (
          <Select<OrdemDasPessoas>
            style={{ minWidth: 200 }}
            value={ordem}
            onChange={(valor) => filtrar({ ordem: valor === 'entrada' ? undefined : valor })}
            options={ORDENS.map((valor) => ({ value: valor, label: ORDEM_LABEL[valor] }))}
          />
        )}
        <Input.Search
          allowClear
          placeholder="Buscar por nome ou e-mail"
          style={{ width: 260 }}
          defaultValue={busca}
          onSearch={(valor) => filtrar({ busca: valor.trim() })}
        />
        <Select<EtapaDaPessoa>
          allowClear
          placeholder="Qualquer etapa"
          style={{ minWidth: 200 }}
          value={etapa}
          onChange={(valor) => filtrar({ etapa: valor })}
          options={ETAPAS.map((valor) => ({
            value: valor,
            label: ETAPA_LABEL[valor],
            title: ETAPA_AJUDA[valor],
          }))}
        />
        <Select<RecorrenciaDaPessoa>
          allowClear
          placeholder="Qualquer recorrência"
          style={{ minWidth: 190 }}
          value={recorrencia}
          onChange={(valor) => filtrar({ recorrencia: valor })}
          options={RECORRENCIAS.map((valor) => ({
            value: valor,
            label: RECORRENCIA_LABEL[valor],
            title: RECORRENCIA_AJUDA[valor],
          }))}
        />
        <Select<SituacaoDaPessoa>
          allowClear
          placeholder="Qualquer acesso"
          style={{ minWidth: 170 }}
          value={situacao}
          onChange={(valor) => filtrar({ situacao: valor })}
          options={SITUACOES.map((valor) => ({ value: valor, label: SITUACAO_LABEL[valor] }))}
        />
        <Select
          allowClear
          showSearch
          placeholder="Qualquer canal"
          style={{ minWidth: 170 }}
          value={canal}
          onChange={(valor) => filtrar({ canal: valor })}
          options={[
            /*
             * A linha sem medição é opção, e não ausência de filtro: no primeiro
             * mês ela é a maior de todas, e não poder isolá-la esconderia quem
             * chegou antes de a medição existir.
             */
            { value: 'direto', label: 'Direto ou sem medição' },
            ...canais.map((valor) => ({ value: valor, label: valor })),
          ]}
        />
        <Select<Role>
          allowClear
          placeholder="Qualquer nível"
          style={{ minWidth: 160 }}
          value={papel}
          onChange={(valor) => filtrar({ papel: valor })}
          options={ROLES.map((valor) => ({
            value: valor,
            label: ROLE_LABEL[valor],
            title: ROLE_DESCRIPTION[valor],
          }))}
        />
        <Tooltip
          title={
            pedeContaDaCasa
              ? 'O filtro de nível ou de acesso já pede contas da equipe.'
              : 'Contas da equipe e contas marcadas como teste. Ficam fora do funil, e por isso ficam fora da lista por padrão.'
          }
        >
          <Checkbox
            checked={mostrarInternas}
            disabled={pedeContaDaCasa}
            onChange={(evento) =>
              filtrar({ internas: evento.target.checked ? 'mostrar' : undefined })
            }
            style={{ alignSelf: 'center' }}
          >
            Mostrar contas internas
          </Checkbox>
        </Tooltip>
      </Flex>

      {pessoas.error ? (
        <Alert type="error" showIcon title={(pessoas.error as Error).message} />
      ) : null}

      {telas.md ? (
        <Card styles={{ body: { padding: 0 } }}>
          <Table<PessoaNaLista>
            rowKey="id"
            loading={pessoas.isPending}
            dataSource={pessoas.data?.items ?? []}
            columns={colunas}
            /*
             * A linha inteira abre a pessoa, e o endereço atual viaja junto:
             * quem volta da ficha cai na mesma página, com os mesmos filtros - e
             * não no topo de uma lista sem recorte.
             */
            onRow={(linha) => ({ onClick: () => abrir(linha.id), style: { cursor: 'pointer' } })}
            onChange={ordenar}
            locale={{ emptyText: vazio }}
            pagination={
              comPaginacao
                ? {
                    ...paginacao,
                    showTotal: (quantas: number) =>
                      `${quantas.toLocaleString('pt-BR')} ${quantas === 1 ? 'pessoa' : 'pessoas'}`,
                    /*
                     * O cartão não tem recuo, para a tabela ir de borda a borda,
                     * e a paginação da `Table` só tem margem vertical: sem isto o
                     * seletor de tamanho encostava na borda do cartão. O recuo é
                     * o mesmo das células.
                     */
                    style: { marginInline: token.padding },
                  }
                : false
            }
            /*
             * Largura fixa, e não `true`: com `scroll={{ x: true }}` a última
             * coluna some atrás da borda em telas médias - já aconteceu nesta
             * casa, e o conserto é um número. É a soma das colunas, e ela cabe
             * nos 1.247px que a área tem num notebook de 1.536 com a barra
             * aberta; abaixo disso, a tabela rola.
             *
             * E o `tableLayout` fixo é o que faz as larguras valerem: no
             * automático, com a área maior que a soma, o navegador redistribui
             * pelo conteúdo - a Origem, que não quebra linha, engordava e
             * espremia o Uso até "10 resp. · 3 resol. · 2 art." virar duas
             * linhas. Cada largura aqui foi medida no navegador em 29/09/2026
             * contra o rótulo mais longo que a coluna mostra.
             */
            tableLayout="fixed"
            scroll={{ x: 1240 }}
          />
        </Card>
      ) : (
        <Card size="small">
          {noCelular}
          {/*
            O seletor de tamanho vai junto, e o próprio Antd o esconde abaixo de
            576px: no telefone vale o tamanho que veio na URL.
          */}
          {comPaginacao ? (
            <Flex justify="center" style={{ marginTop: token.margin }}>
              <Pagination {...paginacao} simple />
            </Flex>
          ) : null}
        </Card>
      )}
    </Space>
  );
}
